import { useEffect, useMemo } from 'react';
import { fromJS } from 'immutable';
import { isJourneyDrilldownReport } from 'reporting-snowflake/funnel/types/journeyReport';
import { useJourneyReport } from './useJourneyReport';
import useJourneyReportOptions from './internal/useJourneyReportOptions';
import Visualization from 'reporting-visualizations/Visualization';
import ResponsiveReportContainer from '../shared/ResponsiveReportContainer';
import { FUNNEL } from 'reporting-data/constants/chartTypes';
import { LoadingSpinner } from 'foundations-components/transitional/components/LoadingSpinner';
import getErrorTypeFromException from 'reporting-data/lib/getErrorTypeFromException';
//@ts-expect-error untyped dependency
import ErrorMessage from '../shared/ErrorMessage';
import NoData from '../shared/NoData';
import { isNoDataState } from 'reporting-snowflake/funnel/utils/journeyUtils';
//@ts-expect-error untyped dependency
import SnowflakePaginator from '../snowflake/SnowflakePaginator';
import useUpdatingRef from '../shared/hooks/useUpdatingRef';
import useReportingIngestionInteraction from '../shared/hooks/useReportingIngestionInteraction';
import emptyFunction from 'react-utils/emptyFunction';
//@ts-expect-error untyped dependency
import { useDifferent } from '../snowflake/internal/queryUtils';
import JourneyLoadingOverlay from './internal/JourneyLoadingOverlay';
import { jsx as _jsx } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
const renderError = error => /*#__PURE__*/_jsx(ResponsiveReportContainer, {
  children: /*#__PURE__*/_jsx(ErrorMessage, {
    type: getErrorTypeFromException(error),
    error: error
  })
});
const renderVisualization = (visualization, {
  isMobile,
  isDrilldown,
  reportOptions,
  handlePageChange,
  handleLimitChange,
  visualizationDebug,
  vizContainerStyleProps
}) => /*#__PURE__*/_jsxs(ResponsiveReportContainer, {
  isXYVisual: visualization.type === FUNNEL,
  isMobile: isMobile,
  "data-test-id": "journey-report-loaded",
  children: [/*#__PURE__*/_jsx(Visualization, {
    visualization: visualization,
    debug: visualizationDebug,
    vizContainerStyleProps: vizContainerStyleProps
  }), isDrilldown ? /*#__PURE__*/_jsx(SnowflakePaginator, {
    reportOptions: reportOptions,
    dataset: visualization.getIn(['data', 'primary']).toJS(),
    isMobile: isMobile,
    onLimitChange: handleLimitChange,
    onPageChange: handlePageChange
  }) : null]
});
const renderLoading = showOverlay => showOverlay ? /*#__PURE__*/_jsx(JourneyLoadingOverlay, {}) : /*#__PURE__*/_jsx(LoadingSpinner, {
  layout: "centered"
});
const renderNoDataState = ({
  isMobile,
  visualizationDebug
}) => /*#__PURE__*/_jsx(ResponsiveReportContainer, {
  "data-test-id": "journey-report-loaded",
  children: /*#__PURE__*/_jsx(NoData, {
    isMobile: isMobile,
    visualizationDebug: visualizationDebug
  })
});
function JourneyReportResolver({
  report,
  isMobile,
  initialReportOptions = {},
  errorRenderer = renderError,
  showOverlay,
  onDrilldown,
  onReportResolve,
  onError,
  debug,
  visualizationDebug,
  vizContainerStyleProps
}) {
  const reportObj = useMemo(() => {
    return report.toJS();
  }, [report]);
  const safeDebug = useMemo(() => debug ? debug.set : emptyFunction, [debug]);
  const ingestionInteraction = useReportingIngestionInteraction(report);
  const isDrilldown = isJourneyDrilldownReport(reportObj);
  const {
    reportOptions,
    handleLimitChange,
    handlePageChange
  } = useJourneyReportOptions(initialReportOptions, isDrilldown);
  const {
    visualization,
    datamap,
    settled,
    error,
    report: journeyReport
  } = useJourneyReport(reportObj, reportOptions, {
    onDrilldown
  }, safeDebug);
  const onReportResolveRef = useUpdatingRef(onReportResolve);
  const onErrorRef = useUpdatingRef(onError);
  const settledChanged = useDifferent(settled);
  useEffect(() => {
    const resolvedReport = {
      resolveDatamap: datamap,
      resolveJourneyReport: journeyReport,
      resolveError: error,
      hasData: !isNoDataState(visualization, isDrilldown)
    };
    if (settledChanged && settled) {
      if (error && onErrorRef.current) {
        ingestionInteraction.logError(resolvedReport.resolveError);
        onErrorRef.current(resolvedReport.resolveError);
      }
      if (onReportResolveRef.current) {
        ingestionInteraction.logSuccess();
        onReportResolveRef.current(fromJS(resolvedReport));
      }
    }
  }, [settled, error, visualization, datamap, journeyReport, onErrorRef, onReportResolveRef, isDrilldown, ingestionInteraction, settledChanged]);
  if (datamap) {
    safeDebug('Data', datamap);
  }
  if (error) {
    safeDebug('Error', error);
    return errorRenderer(error, report);
  }
  if (visualization) {
    safeDebug('Visualization', visualization);
    if (isNoDataState(visualization, isDrilldown)) {
      return renderNoDataState({
        isMobile,
        visualizationDebug
      });
    }
    return renderVisualization(visualization, {
      isMobile,
      isDrilldown,
      reportOptions,
      handleLimitChange,
      handlePageChange,
      visualizationDebug,
      vizContainerStyleProps
    });
  }
  return renderLoading(showOverlay);
}
export default JourneyReportResolver;